// Privacy Policy page — public, routed at /privacy, linked from the footer.
//
// ⚠️ BEFORE LAUNCH (Jul 14, 2026): this is a thorough, accurate draft grounded
// in what the app actually does, but it MUST be reviewed by counsel and have
// these details confirmed/filled:
//   • Legal entity name (currently "Ad Astronaut" — add the registered
//     company name + jurisdiction if incorporated).
//   • Contact email — assumes privacy@adastronaut.com exists; wire it up.
//   • A business mailing address (recommended for GDPR/CCPA completeness).
//   • The sub-processor list below reflects the current stack; keep it in sync
//     when providers are added/removed.
// Consider running it through Termly / Iubenda for a jurisdiction-checked pass.

import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const LAST_UPDATED = 'July 14, 2026';
const CONTACT_EMAIL = 'privacy@adastronaut.com';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-xl md:text-2xl font-bold text-dark mb-3">{title}</h2>
      <div className="space-y-3 text-dark-muted text-sm md:text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}

const SUBPROCESSORS: { name: string; purpose: string }[] = [
  { name: 'Google Cloud Platform & Firebase', purpose: 'Hosting, authentication, database, and file storage' },
  { name: 'Stripe', purpose: 'Payment processing and subscription billing' },
  { name: 'OpenAI', purpose: 'AI image generation and text generation' },
  { name: 'Google (Gemini)', purpose: 'AI image generation' },
  { name: 'Ideogram', purpose: 'AI image generation' },
  { name: 'Black Forest Labs (FLUX)', purpose: 'AI image generation' },
  { name: 'Apify', purpose: 'Public competitor-ad data collection' },
  { name: 'ScrapingBee', purpose: 'Fetching public web pages you provide (brand / product URLs)' },
  { name: 'Twilio', purpose: 'SMS/WhatsApp messaging (only if you enable it)' },
  { name: 'Meta Platforms (Facebook / Instagram)', purpose: 'Publishing content to accounts you connect' },
  { name: 'Sentry', purpose: 'Error monitoring and diagnostics' },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <div className="mb-10">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-dark mb-2">
              Privacy Policy
            </h1>
            <p className="text-sm text-dark-muted">Last updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-10">
            <Section id="intro" title="Introduction">
              <p>
                Ad Astronaut (&ldquo;Ad Astronaut&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
                &ldquo;our&rdquo;) provides an AI-powered tool for creating social media content from
                your products. This Privacy Policy explains what information we collect, how we use
                and share it, and the choices you have. It applies to our website and application
                (the &ldquo;Service&rdquo;).
              </p>
              <p>
                By using the Service, you agree to the collection and use of information as described
                here. If you do not agree, please do not use the Service.
              </p>
            </Section>

            <Section id="collect" title="Information We Collect">
              <p>We collect the following categories of information:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <span className="font-semibold text-dark">Account information.</span> When you sign
                  up we collect your email address, a password (stored securely by our authentication
                  provider, never in plain text), and optionally a display name.
                </li>
                <li>
                  <span className="font-semibold text-dark">Content you provide.</span> Product
                  images you upload, brand details (logo, colors, name, description), product and
                  brand URLs you paste, generated images and creatives, captions, and any text you
                  add. When you paste a URL, we fetch that public web page to extract product or
                  brand information.
                </li>
                <li>
                  <span className="font-semibold text-dark">Payment information.</span> When you
                  purchase a subscription or credits, payments are processed by Stripe. We do not
                  store your full card number. We retain your subscription plan, credit balance,
                  transaction history, and a Stripe customer identifier.
                </li>
                <li>
                  <span className="font-semibold text-dark">Connected accounts.</span> If you connect
                  a Facebook Page or Instagram Business account to publish content, we store the
                  access tokens and account identifiers needed to post on your behalf. You can
                  disconnect at any time.
                </li>
                <li>
                  <span className="font-semibold text-dark">Phone number.</span> Only if you choose
                  to enable SMS/WhatsApp commands, we store your phone number to send and receive
                  those messages.
                </li>
                <li>
                  <span className="font-semibold text-dark">Automatically collected data.</span>
                  Basic technical data such as IP address, browser type, and usage/diagnostic
                  information collected through our error-monitoring provider to keep the Service
                  reliable.
                </li>
              </ul>
            </Section>

            <Section id="use" title="How We Use Your Information">
              <ul className="list-disc pl-5 space-y-2">
                <li>To provide, operate, and improve the Service, including generating your creatives.</li>
                <li>To authenticate you and secure your account.</li>
                <li>To process payments, manage subscriptions, and track your credit balance.</li>
                <li>To publish content to social accounts you explicitly connect and authorize.</li>
                <li>To send SMS/WhatsApp messages you request (if enabled).</li>
                <li>To respond to your requests and provide support.</li>
                <li>To monitor for errors, prevent abuse, and comply with legal obligations.</li>
              </ul>
            </Section>

            <Section id="ai" title="AI Processing">
              <p>
                To generate your creatives, we send the product images you upload and the prompts we
                construct to third-party AI providers (see the sub-processors below). These providers
                process that content to return generated images and text. We do not use your content
                to train our own models. Each provider handles data under its own terms and privacy
                policy; we encourage you to review them and to avoid uploading sensitive personal
                information you do not want processed by these services.
              </p>
            </Section>

            <Section id="share" title="How We Share Your Information">
              <p>We do not sell your personal information. We share information only as follows:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <span className="font-semibold text-dark">Service providers (sub-processors).</span>
                  We share information with the vendors listed below strictly to operate the Service.
                </li>
                <li>
                  <span className="font-semibold text-dark">Social platforms you connect.</span> When
                  you choose to publish, we send your content and captions to the platform (e.g.
                  Facebook or Instagram) on your behalf.
                </li>
                <li>
                  <span className="font-semibold text-dark">Legal and safety.</span> We may disclose
                  information if required by law, to enforce our terms, or to protect the rights,
                  safety, and security of our users or the public.
                </li>
                <li>
                  <span className="font-semibold text-dark">Business transfers.</span> If we are
                  involved in a merger, acquisition, or asset sale, information may be transferred as
                  part of that transaction.
                </li>
              </ul>
            </Section>

            <Section id="subprocessors" title="Sub-processors">
              <p>We rely on the following third-party services to provide the Service:</p>
              <div className="mt-2 overflow-hidden rounded-xl border border-surface-dark/40">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-dark/40 text-left">
                      <th className="px-4 py-2.5 font-semibold text-dark">Provider</th>
                      <th className="px-4 py-2.5 font-semibold text-dark">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SUBPROCESSORS.map((s, i) => (
                      <tr key={s.name} className={i % 2 ? 'bg-white' : 'bg-surface/60'}>
                        <td className="px-4 py-2.5 font-medium text-dark align-top">{s.name}</td>
                        <td className="px-4 py-2.5 text-dark-muted align-top">{s.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section id="retention" title="Data Retention">
              <p>
                We retain your account information and content for as long as your account is active.
                If you delete specific creatives, they are removed from your library. If you close
                your account, we delete or de-identify your personal information within a reasonable
                period, except where we must retain certain records (for example, transaction records
                for tax, accounting, or legal compliance).
              </p>
            </Section>

            <Section id="security" title="Security">
              <p>
                We use industry-standard measures to protect your information, including encryption in
                transit, access controls, and reputable cloud infrastructure. No method of
                transmission or storage is completely secure, so we cannot guarantee absolute
                security, but we work to protect your data and to promptly address any issues.
              </p>
            </Section>

            <Section id="rights" title="Your Rights &amp; Choices">
              <p>
                Depending on your location, you may have rights to access, correct, export, or delete
                your personal information, and to object to or restrict certain processing. These
                include rights under the EU/UK GDPR and the California Consumer Privacy Act (CCPA).
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>You can update your account details from your profile settings.</li>
                <li>You can disconnect any linked social account at any time.</li>
                <li>
                  You can request account deletion, or the export or deletion of your personal
                  information, by contacting us at{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary font-semibold hover:underline">
                    {CONTACT_EMAIL}
                  </a>
                  .
                </li>
              </ul>
              <p>We will not discriminate against you for exercising any of these rights.</p>
            </Section>

            <Section id="cookies" title="Cookies &amp; Local Storage">
              <p>
                We use cookies and browser local storage that are necessary for the Service to
                function — for example, to keep you signed in and to remember interface preferences.
                We do not currently use third-party advertising cookies.
              </p>
            </Section>

            <Section id="transfers" title="International Data Transfers">
              <p>
                We and our sub-processors may process and store information in countries other than
                your own, including the United States. Where required, we rely on appropriate
                safeguards for such transfers.
              </p>
            </Section>

            <Section id="children" title="Children&rsquo;s Privacy">
              <p>
                The Service is not directed to children under 13 (or the minimum age required in your
                jurisdiction), and we do not knowingly collect their personal information. If you
                believe a child has provided us information, please contact us and we will delete it.
              </p>
            </Section>

            <Section id="changes" title="Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. When we do, we will revise the
                &ldquo;Last updated&rdquo; date above and, where appropriate, provide additional
                notice. Your continued use of the Service after changes take effect constitutes
                acceptance of the updated policy.
              </p>
            </Section>

            <Section id="contact" title="Contact Us">
              <p>
                If you have questions about this Privacy Policy or how we handle your information,
                contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary font-semibold hover:underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
